import React, { useCallback, useEffect, useState } from 'react';
import { useTable, Column } from 'react-table';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Paging from '../../common/layouts/page/Paging';

import { getHoldAuthorList, updateAuthorStatus } from '../../api';
import RejectReasonModal from '../../common/layouts/modal/RejectReasonModal';

interface Client {
    avatar: string;
    name: string;
    email: string;
}

interface TableData {
    authorName: string;
    authorEmail: string;
    accountStatus: string;
}

interface AuthorData {
    id: number;
    profilePhoto?: string;
    authorName: string;
    authorIntro?: string;
    authorDescription?: string;
}

interface statusData {
    status: string | string[];
}

interface TestMap {
    page?: string;
    size?: string;
    authorStatus?: string;
}

const AuthorTable = ({ status }: statusData) => {

    const [authors, setAuthors] = useState<any[]>([]); // 작가 목록 상태 추가
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [selectedAuthor, setSelectedAuthor] = useState<any>(null);
    const [showRejectModal, setShowRejectModal] = useState(false);

    const onPageChange = useCallback((page: number) => {
        setCurrentPage(page); // 현재 페이지 업데이트
    }, []);

    const handleApprove = async (author: any) => {
        const confirmApprove = window.confirm(`${author.authorName} 님의 작가 신청을 승인하시겠습니까?`);
        if (!confirmApprove) return;

        try {
            await updateAuthorStatus(author.authorEmail, 'ACTIVE');
            alert("승인이 완료되었습니다.");
            fetchAuthors();
        } catch (error) {
            console.error("Error approving author:", error);
            alert("승인 처리에 실패했습니다.");
        }
    };

    const handleRejectClick = (author: any) => {
        setSelectedAuthor(author);
        setShowRejectModal(true);
    };

    // 데이터 요청 함수
    const fetchAuthors = useCallback(async () => {
        const loginData = {
            id: 'admin',
            password: 'admin',
        };

        try {
            const adminToken = sessionStorage.getItem('adminToken');

            if (adminToken) {
                const currentStatus = Array.isArray(status) ? status[0] : status;
                let data;

                if (currentStatus === 'HOLD') {
                    // 비활성화(HOLD) 상태일 때 getHoldAuthorList API 사용
                    data = await getHoldAuthorList(currentPage, 10);
                } else {
                    const apiUrl = '/api/admin/arth/list';

                    const statusArray = Array.isArray(status) ? status : [status];
                    const testMap: TestMap = {
                        authorStatus: statusArray.join(','),
                    };

                    const response = await axios.post(
                        apiUrl, // 결정된 URL 사용
                        testMap,
                        {
                            headers: {
                                Authorization: `Bearer ${adminToken}`,
                            },
                            params: {
                                page: currentPage,
                                size: 10,
                            },
                        }
                    );
                    data = response.data;
                }

                // console.log(data);

                const content = data.content || [];
                setAuthors(content);

                // 페이징 정보 업데이트
                setTotalPages(data.totalPages || 1);
            }
        } catch (error) {
            console.error('Error fetching authors:', error);
        }
    }, [currentPage, status]); // currentPage, status 변경 시 다시 실행

    // 데이터 불러오기
    useEffect(() => {
        fetchAuthors();
    }, [fetchAuthors]);

    // 계정 상태 영어 값 -> 한글 값 매핑
    const statusMap: { [key: string]: string } = {
        ACTIVE: '승인',
        PENDING: '승인 대기',
        SUSPENDED: '반려',
        HOLD: '비활성화',
    };

    const isPendingList = status === 'PENDING';

    let columns: Column<TableData>[] = React.useMemo(
        () => {
            const baseCols: Column<TableData>[] = [
                {
                    Header: '작가명',
                    accessor: 'authorName',
                },
                {
                    Header: '이메일',
                    accessor: 'authorEmail',
                },
                {
                    Header: '계정 상태',
                    accessor: 'accountStatus',
                    Cell: ({ value }: { value: string }): React.ReactElement => {
                        return <span>{statusMap[value] || value}</span>;
                    },
                },
            ];

            if (isPendingList) {
                baseCols.push({
                    Header: '액션',
                    id: 'action',
                    Cell: ({ row }: { row: any }): React.ReactElement => {
                        const author = row.original;
                        return (
                            <div className="d-flex gap-2 pe-4" style={{ whiteSpace: 'nowrap' }}>
                                <button
                                    onClick={() => handleApprove(author)}
                                    className="btn btn-xs btn-outline-success px-2.5 py-1 fs-11 fw-bold rounded"
                                >
                                    승인
                                </button>
                                <button
                                    onClick={() => handleRejectClick(author)}
                                    className="btn btn-xs btn-outline-danger px-2.5 py-1 fs-11 fw-bold rounded"
                                >
                                    반려
                                </button>
                            </div>
                        );
                    }
                });
            }

            return baseCols;
        },
        [authors, status, isPendingList]
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
        columns,
        data: authors,
    });

    return (
        <div className="main-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card stretch stretch-full border-0 shadow-sm">
                        <div className="card-body p-0">
                            <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', minHeight: '180px' }}>
                                <table className="table table-hover mb-0" id="proposalList" {...getTableProps()} style={{ minWidth: '900px', tableLayout: 'fixed' }}>
                                    <thead>
                                        {headerGroups.map((headerGroup) => (
                                            <tr {...headerGroup.getHeaderGroupProps()}>
                                                {headerGroup.headers.map((column) => {
                                                    const isAction = column.id === 'action';
                                                    const isStatus = column.id === 'accountStatus';
                                                    const isName = column.id === 'authorName';
                                                    return (
                                                        <th 
                                                            {...column.getHeaderProps()}
                                                            style={{
                                                                padding: isAction ? '0.85rem 2.5rem 0.85rem 1.25rem' : '0.85rem 1.25rem',
                                                                width: isAction ? '185px' : isStatus ? '130px' : isName ? '180px' : 'auto',
                                                                minWidth: isAction ? '185px' : isStatus ? '130px' : isName ? '180px' : 'auto'
                                                            }}
                                                        >
                                                            {column.render('Header')}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                        {rows.map((row) => {
                                            prepareRow(row);
                                            return (
                                                <tr {...row.getRowProps()}>
                                                    {row.cells.map((cell) => {
                                                        const isAction = cell.column.id === 'action';
                                                        const isStatus = cell.column.id === 'accountStatus';
                                                        const isName = cell.column.id === 'authorName';
                                                        return (
                                                            <td 
                                                                {...cell.getCellProps()}
                                                                style={{ 
                                                                    verticalAlign: 'middle',
                                                                    padding: isAction ? '0.85rem 2.5rem 0.85rem 1.25rem' : '0.85rem 1.25rem',
                                                                    width: isAction ? '185px' : isStatus ? '130px' : isName ? '180px' : 'auto',
                                                                    minWidth: isAction ? '185px' : isStatus ? '130px' : isName ? '180px' : 'auto'
                                                                }}
                                                            >
                                                                {cell.column.id === 'authorName' ? (
                                                                    <Link
                                                                        to={`/adminAuthorDetail`}
                                                                        state={{
                                                                            author: authors.find(
                                                                                (author) => author.authorEmail === row.original.authorEmail
                                                                            ),
                                                                            status: status,
                                                                        }}
                                                                        className="text-decoration-none fw-bold text-[#6366f1] hover:text-[#4f46e5]"
                                                                    >
                                                                        {cell.render('Cell')}
                                                                    </Link>
                                                                ) : cell.column.id === 'accountStatus' ? (
                                                                     <span
                                                                         className="badge px-2.5 py-1.5 fs-11 border-0 rounded-pill fw-bold"
                                                                         style={{
                                                                             backgroundColor: cell.value === 'ACTIVE' ? '#ecfdf5' :
                                                                                              cell.value === 'PENDING' ? '#fffbeb' :
                                                                                              cell.value === 'SUSPENDED' ? '#fef2f2' : '#f1f5f9',
                                                                             color: cell.value === 'ACTIVE' ? '#10b981' :
                                                                                    cell.value === 'PENDING' ? '#f59e0b' :
                                                                                    cell.value === 'SUSPENDED' ? '#ef4444' : '#64748b'
                                                                         }}
                                                                     >
                                                                         {statusMap[cell.value] || cell.value}
                                                                     </span>
                                                                ) : (
                                                                    cell.render('Cell')
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="card-footer bg-white border-top-0 d-flex justify-content-center py-4">
                            <Paging totalPages={totalPages} currentPage={currentPage} onPageChange={onPageChange} />
                        </div>
                    </div>
                </div>
            </div>
            {showRejectModal && selectedAuthor && (
                <RejectReasonModal
                    authorName={selectedAuthor.authorName}
                    authorEmail={selectedAuthor.authorEmail}
                    onClose={() => {
                        setShowRejectModal(false);
                        setSelectedAuthor(null);
                    }}
                    onSuccess={() => {
                        setShowRejectModal(false);
                        setSelectedAuthor(null);
                        fetchAuthors(); // 성공 시 리스트 갱신
                    }}
                />
            )}
        </div>
    );
};

export default AuthorTable;
